import { NavLink } from 'react-router-dom';
import { NAV_GROUP_LABEL, NAV_ITEMS, type NavGroup, type NavItem } from '../../routes';
import { MOCK_FORCED } from '../../lib/api';
import { useCompany } from '../../lib/companyContext';
import { formatPercentFromBps } from '../../lib/format';
import { ThemeToggle } from '../ThemeToggle';
import { Badge, CompanyLogo, Icon, Select } from '../ui';

/**
 * Conteudo da barra lateral (RNF16).
 *
 * Deliberadamente sem posicionamento proprio: o componente so preenche a altura
 * do container. Quem decide se isso e uma coluna fixa do desktop ou o painel do
 * drawer e o AppShell -- assim o MESMO menu serve os dois casos.
 *
 * Dois grupos: "Empresa" (o CRM do lojista, escopado a empresa em foco) e
 * "Converte Facil · administracao" (so `platform_admin`). O cartao do rodape
 * mostra a empresa aberta (RNF12) e, para a equipe da plataforma, o seletor
 * que troca o CRM em foco -- o lojista nunca ve esse seletor.
 *
 * Interacao (pedido do dono, 25/09/2026):
 *  - cada item responde ao mouse com tinta da marca, icone aceso, deslize de
 *    2px e o atalho de teclado (Alt+N) aparecendo a direita; o clique tem um
 *    leve "afundar" (active:scale) para a acao ter peso;
 *  - a lateral RECOLHE para so icones no desktop (botao no rodape; o AppShell
 *    guarda a escolha). Recolhida, cada item mostra o nome num balao ao passar
 *    o mouse ou ao receber foco -- nunca depende do mouse para ser usavel;
 *  - o rodape diz de onde vem o dado (previa simulada x sessao real), para o
 *    numero na tela nunca ser confundido com producao.
 */

interface SidebarProps {
  readonly onNavigate?: (() => void) | undefined;
  /** So no desktop: icones apenas, com baloes. O drawer sempre passa false. */
  readonly collapsed?: boolean;
  readonly onToggleCollapse?: (() => void) | undefined;
}

const GRUPOS: readonly NavGroup[] = ['empresa', 'plataforma'];

export function visibleNavItems(isPlatformAdmin: boolean): readonly NavItem[] {
  return NAV_ITEMS.filter((item) => !item.adminOnly || isPlatformAdmin);
}

export function Sidebar({ onNavigate, collapsed = false, onToggleCollapse }: SidebarProps): JSX.Element {
  const { company, companies, isPlatformAdmin, selectCompany } = useCompany();
  const visiveis = visibleNavItems(isPlatformAdmin);

  return (
    <div className={`flex h-full flex-col gap-5 bg-surface py-5 ${collapsed ? 'px-2' : 'px-3'}`}>
      <div className={`flex items-center gap-3 ${collapsed ? 'justify-center px-0' : 'px-2'}`}>
        <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-hero text-sm font-bold text-white shadow-soft">
          CF
        </span>
        {collapsed ? null : (
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-sm font-semibold text-ink">Converte Fácil</span>
            <span className="truncate text-xs text-ink-subtle">Recuperação de vendas</span>
          </span>
        )}
      </div>

      <nav aria-label="Navegação principal" className="min-h-0 flex-1 overflow-y-auto overflow-x-visible">
        {GRUPOS.map((grupo) => {
          const itens = visiveis.filter((i) => i.group === grupo);
          if (itens.length === 0) {
            return null;
          }
          return (
            <div key={grupo} className="mb-4">
              {collapsed ? (
                <div aria-hidden="true" className="mx-2 my-2 h-px bg-line" />
              ) : (
                <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">{NAV_GROUP_LABEL[grupo]}</p>
              )}
              <ul className="flex flex-col gap-1">
                {itens.map((item) => (
                  <li key={item.to}>
                    <ItemDeMenu item={item} atalho={visiveis.indexOf(item) + 1} collapsed={collapsed} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="flex flex-col gap-3">
        <div className="sm:hidden">
          <ThemeToggle />
        </div>

        {collapsed ? (
          <div className="group relative flex justify-center" data-slot="empresa-da-sessao">
            <CompanyLogo company={company} size="md" />
            <Balao>{company.name}</Balao>
          </div>
        ) : (
          <div data-slot="empresa-da-sessao" className="rounded-xl border border-line bg-elevated px-3 py-3 shadow-soft transition-colors hover:border-edge">
            <p className="text-[10px] font-medium uppercase tracking-wide text-ink-subtle">{isPlatformAdmin ? 'CRM em foco' : 'Empresa'}</p>
            <div className="mt-1.5 flex items-center gap-2.5">
              <CompanyLogo company={company} size="md" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{company.name}</p>
                <p className="truncate text-xs text-ink-subtle">Comissão de {formatPercentFromBps(company.feeBps)} sobre o recuperado</p>
              </div>
            </div>
            {isPlatformAdmin && companies.length > 1 ? (
              <label className="mt-2.5 block text-xs text-ink-subtle">
                <span className="sr-only">Trocar a empresa em foco</span>
                <Select size="sm" value={company.id} onChange={(e) => selectCompany(e.target.value)} aria-label="Trocar a empresa em foco">
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </label>
            ) : null}
          </div>
        )}

        <div className={`flex items-center ${collapsed ? 'flex-col gap-2' : 'justify-between gap-2'}`}>
          <span className={collapsed ? 'group relative' : ''}>
            <Badge tone={MOCK_FORCED ? 'warning' : 'good'} dot className={collapsed ? 'px-1.5' : ''}>
              {collapsed ? '' : MOCK_FORCED ? 'Prévia · dados simulados' : 'Sessão ativa'}
            </Badge>
            {collapsed ? <Balao>{MOCK_FORCED ? 'Prévia · dados simulados' : 'Sessão ativa'}</Balao> : null}
          </span>
          {onToggleCollapse === undefined ? null : (
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-pressed={collapsed}
              aria-label={collapsed ? 'Expandir a barra lateral' : 'Recolher a barra lateral'}
              title={collapsed ? 'Expandir (Alt+0)' : 'Recolher (Alt+0)'}
              className="group relative hidden h-8 w-8 place-items-center rounded-lg border border-line bg-surface text-ink-muted transition-all hover:border-brand/60 hover:bg-brand/5 hover:text-brand active:scale-95 lg:grid"
            >
              <Icon name={collapsed ? 'seta-dir' : 'seta-esq'} size="sm" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemDeMenu({
  item,
  atalho,
  collapsed,
  onNavigate,
}: {
  readonly item: NavItem;
  readonly atalho: number;
  readonly collapsed: boolean;
  readonly onNavigate?: (() => void) | undefined;
}): JSX.Element {
  const temAtalho = atalho >= 1 && atalho <= 9;
  return (
    <NavLink
      to={{ pathname: item.to, search: window.location.search }}
      end={item.end}
      onClick={onNavigate}
      title={collapsed ? undefined : temAtalho ? `${item.label} (Alt+${atalho})` : item.label}
      className={({ isActive }) =>
        [
          'group relative flex items-center gap-3 rounded-lg border-l-2 py-2 text-sm transition-all duration-150 active:scale-[0.98]',
          collapsed ? 'justify-center px-0' : 'px-2.5',
          isActive
            ? 'border-brand bg-brand/10 font-medium text-ink'
            : 'border-transparent text-ink-muted hover:translate-x-0.5 hover:border-brand/60 hover:bg-brand/5 hover:text-ink',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors duration-150 ${
              isActive ? 'bg-brand text-white shadow-soft' : 'bg-ink/5 text-ink-muted group-hover:bg-brand/10 group-hover:text-brand'
            }`}
          >
            <Icon name={item.icon} size="md" />
          </span>
          {collapsed ? (
            <Balao>
              {item.label}
              {temAtalho ? <kbd className="ml-2 rounded border border-line px-1 text-[10px] text-ink-subtle">Alt+{atalho}</kbd> : null}
            </Balao>
          ) : (
            <>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{item.label}</span>
                <span className="truncate text-xs font-normal text-ink-subtle">{item.hint}</span>
              </span>
              {temAtalho ? (
                <kbd
                  aria-hidden="true"
                  className={`hidden shrink-0 rounded border border-line bg-surface px-1.5 py-0.5 text-[10px] tabular-nums text-ink-subtle transition-opacity duration-150 lg:inline ${
                    isActive ? 'opacity-70' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
                  }`}
                >
                  Alt+{atalho}
                </kbd>
              ) : null}
              <Icon
                name="seta-dir"
                size="xs"
                className={`shrink-0 text-brand transition-all duration-150 ${isActive ? 'opacity-0' : '-translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'}`}
              />
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

/** Balao que aparece a direita do item recolhido, no hover e no foco. */
function Balao({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <span
      role="tooltip"
      className="pointer-events-none absolute left-full top-1/2 z-drawer ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg border border-line bg-elevated px-2.5 py-1.5 text-xs font-medium text-ink opacity-0 shadow-float transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
    >
      {children}
    </span>
  );
}
